import { useEffect, createRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../components/ui/button';
import { X as XIcon, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { cldFull } from '../../lib/cloudinary';

const PhotoLightbox = ({ photos, lightbox, setLightbox, downloadFromUrl }) => {
  // Keyboard nav
  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox({ open: false, index: 0 });
      else if (e.key === 'ArrowRight') setLightbox((l) => ({ ...l, index: (l.index + 1) % Math.max(photos.length, 1) }));
      else if (e.key === 'ArrowLeft') setLightbox((l) => ({ ...l, index: (l.index - 1 + photos.length) % Math.max(photos.length, 1) }));
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox.open, photos.length, setLightbox]);

  if (!lightbox.open || !photos[lightbox.index]) return null;

  const total = photos.length;
  const cur = photos[lightbox.index];
  const fullUrl = cldFull(cur.cloudinary_url || cur.url);
  const filename = `Cardoso_${(cur.entry_date || 'foto').replace(/[^a-z0-9-]/gi, '_')}_${lightbox.index + 1}.jpg`;
  const close = () => setLightbox({ open: false, index: 0 });
  const next = () => setLightbox((l) => ({ ...l, index: (l.index + 1) % total }));
  const prev = () => setLightbox((l) => ({ ...l, index: (l.index - 1 + total) % total }));

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
      onClick={close}
      data-testid="client-photo-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); close(); }}
        className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black/40 rounded-full p-2"
        data-testid="client-lightbox-close"
        aria-label="Sluiten"
      >
        <XIcon className="w-6 h-6" />
      </button>
      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 text-white hover:text-gray-300 bg-black/40 rounded-full p-2"
            data-testid="client-lightbox-prev"
            aria-label="Vorige"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 text-white hover:text-gray-300 bg-black/40 rounded-full p-2"
            data-testid="client-lightbox-next"
            aria-label="Volgende"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </>
      )}
      <div className="max-w-[92vw] max-h-[88vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img src={fullUrl} alt={cur.filename || 'foto'} className="max-w-full max-h-[78vh] object-contain rounded shadow-2xl" />
        <div className="mt-3 flex items-center gap-3">
          <span className="text-white/80 text-xs">{lightbox.index + 1} / {total}</span>
          {cur.entry_date && <span className="text-white/60 text-xs">· {cur.entry_date}</span>}
          {cur.project && <span className="text-white/60 text-xs">· {cur.project}</span>}
          <Button
            size="sm"
            onClick={() => downloadFromUrl(fullUrl, filename)}
            className="bg-[#3da5a5] hover:bg-[#2d8585]"
            data-testid="client-lightbox-download"
          >
            <Download className="w-3 h-3 mr-1" /> Download
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PhotoLightbox;
